import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  // Network timeout — without this, calls hang forever when the
  // backend is overloaded or the proxy is slow, which feels like
  // the app is "offline". 30s lets long aggregations through but
  // surfaces real failures quickly. See OS_ENV_NETWORK_TIMEOUT_MS.
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only force a logout when the session itself is invalid (verified via
      // /auth/me). A 401 from a specific resource endpoint might be a
      // permission/tenant edge case and must NOT wipe the user's session —
      // that was causing people to "get kicked out" and have to re-login.
      const requestUrl = error.config?.url || '';
      const isAuthCheck = requestUrl.includes('/auth/me')
        || requestUrl.includes('/auth/login');

      if (isAuthCheck) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Avoid redirect loop if we're already on /login.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    // If the backend pod has just been redeployed OR is overloaded
    // under traffic spike, Mongo can take a second or two to warm
    // up and the ingress proxy may return 502/504. The server
    // distinguishes "actually wrong credentials" (401) from
    // "transient backend" (503 + Retry-After, plus 502/504 from
    // the proxy and pure network errors). We absorb up to 3 silent
    // retries (~1.5s + ~3.5s) so the operator never sees a
    // misleading "Invalid credentials" toast right after a deploy
    // or during a brief spike.
    const tries = [0, 1500, 3500];   // immediate, +1.5s, +3.5s
    let lastErr;
    for (let i = 0; i < tries.length; i++) {
      if (tries[i]) await new Promise((r) => setTimeout(r, tries[i]));
      try {
        // Login uses a generous 25s timeout — it's a single auth
        // request and we'd rather absorb a slow Atlas response
        // than surface a network error to the user.
        const response = await api.post('/auth/login', formData, { timeout: 25000 });
        return response.data;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // Bubble up REAL auth errors immediately — bad creds, locked
        // account, etc. Otherwise retry on transient backend warming.
        const isRealAuthError = status === 401 || status === 403 || status === 422;
        if (isRealAuthError) throw err;
        // Retry on: 503 (warming), 502/504 (gateway), no response
        // (network error, CORS, ECONNABORTED/timeout).
        const isTransient = status === 503
          || status === 502 || status === 504
          || err.code === 'ECONNABORTED'
          || err.code === 'ERR_NETWORK'
          || !err.response;
        if (!isTransient) throw err;
      }
    }
    // Final-attempt failed → surface a helpful error rather than the
    // backend's last raw response. LoginPage already falls back to
    // "Invalid credentials" when detail is missing, so synthesize a
    // clearer message here.
    const status = lastErr?.response?.status;
    const synthetic = new Error(
      status
        ? `Server is busy (HTTP ${status}). Please try again in a moment.`
        : 'The server is taking too long to respond. Please check your connection and try again.'
    );
    synthetic.response = {
      data: { detail: synthetic.message },
      status: status || 0,
    };
    throw synthetic;
  },
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

export const leadsAPI = {
  getAll: async () => {
    const response = await api.get('/leads');
    // Handle both { leads: [...] } and [...] formats
    return response.data.leads || response.data;
  },
  create: async (leadData) => {
    const response = await api.post('/leads', leadData);
    return response.data;
  },
  update: async (leadId, updateData) => {
    const response = await api.put(`/leads/${leadId}`, updateData);
    return response.data;
  },
};

export const inventoryAPI = {
  getAll: async () => {
    const response = await api.get('/inventory');
    return response.data;
  },
  create: async (itemData) => {
    const response = await api.post('/inventory', itemData);
    return response.data;
  },
};

export const vendorsAPI = {
  getAll: async () => {
    const response = await api.get('/vendors');
    return response.data;
  },
  create: async (vendorData) => {
    const response = await api.post('/vendors', vendorData);
    return response.data;
  },
};

export const purchaseOrdersAPI = {
  getAll: async () => {
    const response = await api.get('/purchase-orders');
    // Handle both { purchase_orders: [...] } and [...] formats
    return response.data.purchase_orders || response.data;
  },
  create: async (poData) => {
    const response = await api.post('/purchase-orders', poData);
    return response.data;
  },
};

export const deliveriesAPI = {
  getAll: async () => {
    const response = await api.get('/deliveries');
    return response.data;
  },
  create: async (deliveryData) => {
    const response = await api.post('/deliveries', deliveryData);
    return response.data;
  },
  update: async (deliveryId, updateData) => {
    const response = await api.put(`/deliveries/${deliveryId}`, updateData);
    return response.data;
  },
};

export const tasksAPI = {
  getAll: async () => {
    const response = await api.get('/tasks');
    return response.data;
  },
  create: async (taskData) => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },
};

export const marketingAPI = {
  getCampaigns: async () => {
    const response = await api.get('/marketing/campaigns');
    return response.data;
  },
  createCampaign: async (campaignData) => {
    const response = await api.post('/marketing/campaigns', campaignData);
    return response.data;
  },
};

export default api;